import { computed, nextTick, ref } from 'vue';
import { useToast } from 'vue-toastification';
import { i18n } from '@/plugins/internationalization/i18n';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import { useRecordFormModal } from '@/composables/records/useRecordFormModal';
import { MedicineInventoryFormId, type Form, type MedicineMovement, type MultiStepFormState, type ResourceForm } from '@/types';

/** What a create form opens with: any field given here arrives filled (and locked by the form itself). */
export type InventoryFormPrefill = {
    farmId?: number;
    drugId?: number;
    unitId?: number;
};

const emptyState = (): MultiStepFormState => ({
    location: null,
    form: null,
    confirmed: false,
    farm: null,
    event: null,
    notification: null,
});

/** The modal's step config: the edit step as given, submitted through `submitActionType`. */
function resourceSteps(editStep: Record<string, unknown>, submitActionType: ActionType): Record<string, unknown>[] {
    return [{}, editStep, { disableRefetch: true, submitActionType }, {}];
}

/** The form a purchase, spillage or count movement edits through, by the movement's type. */
function formIdOf(movement: Pick<MedicineMovement, 'type'>): MedicineInventoryFormId {
    switch (movement.type) {
        case 'purchase':
            return MedicineInventoryFormId.Purchase;
        case 'waste':
            return MedicineInventoryFormId.Waste;
        default:
            return MedicineInventoryFormId.Count;
    }
}

/**
 * The round trips behind every inventory form: fetch the form, load it into the generic `ActionModal`
 * on its edit step and open it.
 *
 * Three kinds of form share the one modal a host renders: a create form (purchase, spillage or count,
 * through `GetForm`/`PutForm`), an edit of a purchase, spillage or count (through the generic resource route), and an
 * edit of a treatment movement, which is the existing registration form reached through
 * `useRecordFormModal` - so a treatment is edited through exactly one form wherever it is reached from.
 */
export function useInventoryFormModal() {
    const record = useRecordFormModal();
    const toast = useToast();

    /** A form that cannot be fetched leaves the click with no visible effect, so the reader is told. */
    const reportOpenFailure = () => toast.error(i18n.global.t('global.errors.unknown'));

    /** The one place a failed open is dealt with: the dispatches skip the central handler, so exactly one toast shows. */
    const REPORTED_HERE = { skipHandleError: true };

    const source = ref<'resource' | 'record'>('resource');
    const resourceStepConfig = ref<Record<string, unknown>[]>(resourceSteps({ editable: true }, ActionType.PutForm));
    const resourceInitialState = ref<MultiStepFormState>(emptyState());
    const loadingKey = ref<string | null>(null);

    const initialState = computed(() => (source.value === 'record' ? record.initialState.value : resourceInitialState.value));
    const stepConfig = computed(() => (source.value === 'record' ? record.stepConfig.value : resourceStepConfig.value));
    const header = computed(() => (source.value === 'record' ? record.header.value : null));

    /** The modal reads its state on open, so the state has to be through the DOM first. */
    async function showModal(open: CallableFunction) {
        await nextTick();

        open();
    }

    async function openCreate(formId: MedicineInventoryFormId, prefill: InventoryFormPrefill, open: CallableFunction) {
        if (loadingKey.value !== null) {
            return;
        }

        try {
            loadingKey.value = `create-${formId}`;

            const form: Form = await store.dispatch(ActionType.GetForm, {
                form: {
                    form_id: formId,
                    location_ids: prefill.farmId !== undefined ? [prefill.farmId] : undefined,
                    resource_id: prefill.drugId,
                    resource_type_ids: prefill.unitId !== undefined ? [prefill.unitId] : undefined,
                },
                options: REPORTED_HERE,
            });

            source.value = 'resource';
            resourceStepConfig.value = resourceSteps({ editable: true }, ActionType.PutForm);
            resourceInitialState.value = { ...emptyState(), form };

            await showModal(open);
        } catch {
            reportOpenFailure();
        } finally {
            loadingKey.value = null;
        }
    }

    async function openMovement(movement: MedicineMovement, open: CallableFunction) {
        if (loadingKey.value !== null || record.loadingRecordId.value !== null) {
            return;
        }

        if (movement.record_id !== null) {
            source.value = 'record';
            try {
                await record.openRecordForm(movement.record_id, open, REPORTED_HERE);
            } catch {
                reportOpenFailure();
            }
            return;
        }

        if (movement.resource_id === null) {
            return;
        }

        await openResource(movement.key, formIdOf(movement), movement.resource_id, open);
    }

    /** Opens the edit form of a purchase, spillage or count by its id; `key` names what the spinner belongs to. */
    async function openResource(key: string, formId: MedicineInventoryFormId, resourceId: number, open: CallableFunction) {
        if (loadingKey.value !== null || record.loadingRecordId.value !== null) {
            return;
        }

        try {
            loadingKey.value = key;

            const resourceForm: ResourceForm = await store.dispatch(ActionType.GetResourceForm, { form: { form_id: formId, resource_id: resourceId }, options: REPORTED_HERE });

            source.value = 'resource';
            resourceStepConfig.value = resourceSteps({ deleteButton: resourceForm.deletable, editable: resourceForm.editable }, ActionType.PutResourceForm);
            resourceInitialState.value = {
                ...emptyState(),
                form: resourceForm.form,
                resource: { form_id: formId, resource_id: resourceId },
            };

            await showModal(open);
        } catch {
            reportOpenFailure();
        } finally {
            loadingKey.value = null;
        }
    }

    /** The movement key whose form is being fetched (or, for a treatment, its record), so that row alone can show a spinner. */
    const loadingMovementKey = computed(() => loadingKey.value);

    return {
        initialState,
        stepConfig,
        header,
        loadingKey: loadingMovementKey,
        loadingRecordId: record.loadingRecordId,
        openCreate,
        openMovement,
        openResource,
    };
}
